import { DragAndDrop } from '@dealstack/frontend/shared/components/DragAndDrop/DragAndDrop';
import {
	AlertDescription,
	AlertTitle,
	Alert,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { ModalContent, Modal } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { flexRender } from '@tanstack/react-table';
import { memo } from 'react';
import styled from 'styled-components';
import { Box, Text } from '@mantine/core';

import {
	CURRENT_VISIBLE_MODAL,
	useCustomDetailsController,
} from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/CustomDetails/useCustomDetailsController';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';

const CustomDetails = () => {
	const {
		currentVisibleModal,
		setCurrentVisibleModal,
		isDataLoading,
		refreshData,
		tableContainerRef,
		tanstackTable,
		rowVirtualizer,
		bulkCreateUpload,
		onDownloadXLSXTemplate,
		customDetailsData,
	} = useCustomDetailsController();

	const totalSize = rowVirtualizer.totalSize;
	const virtualRows = rowVirtualizer.virtualItems;

	const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
	const paddingBottom =
		virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

	return (
		<Box pos="relative">
			<ButtonV2 onClick={() => refreshData()} size="s" variant="tertiary" buttonTheme="light">
				Refresh table
			</ButtonV2>
			<SectionWrapper>
				<BulkCreateSection>
					<Text display="inline" size="B5Medium">
						Add custom details:
					</Text>
					<ButtonV2
						size="s"
						variant="primary"
						buttonTheme="light"
						onClick={onDownloadXLSXTemplate}
					>
						Download XLSX template
					</ButtonV2>
					<BulkUploadEditWrapper>
						<ButtonV2
							onClick={() =>
								setCurrentVisibleModal(CURRENT_VISIBLE_MODAL.BULK_CREATE)
							}
							size="s"
							variant="primary"
							buttonTheme="light"
						>
							Bulk create
						</ButtonV2>
						{currentVisibleModal === CURRENT_VISIBLE_MODAL.BULK_CREATE && (
							<Modal
								onClose={() => setCurrentVisibleModal(CURRENT_VISIBLE_MODAL.NONE)}
							>
								<ModalContent>
									<Alert icon="information-filled" state="warning" size="m">
										<AlertTitle>WARNING</AlertTitle>
										<AlertDescription>
											You can create new custom details by structuring data
											into columns
										</AlertDescription>
									</Alert>
									<br />
									<DragAndDrop
										uploadFileRequest={bulkCreateUpload}
										accept={{
											[EXCEL_SPREADSHEET_TYPE]: ['.xlsx'],
										}}
										invalidTypeMessage="Error"
									/>
								</ModalContent>
							</Modal>
						)}
					</BulkUploadEditWrapper>
				</BulkCreateSection>
			</SectionWrapper>
			<TableWrapper>
				{isDataLoading ? <InlineLoader /> : null}
				<div ref={tableContainerRef} className="table_container">
					<table className="table">
						<thead>
							{tanstackTable.getHeaderGroups().map((headerGroup) => (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<th
												key={header.id}
												colSpan={header.colSpan}
												style={{ width: header.getSize() }}
											>
												{header.isPlaceholder ? null : (
													<div
														{...{
															className: header.column.getCanSort()
																? 'cursor-pointer select-none'
																: '',
															onClick:
																header.column.getToggleSortingHandler(),
														}}
													>
														{flexRender(
															header.column.columnDef.header,
															header.getContext()
														)}
														{{
															asc: ' 🔼',
															desc: ' 🔽',
														}[header.column.getIsSorted() as string] ??
															null}
													</div>
												)}
											</th>
										);
									})}
								</tr>
							))}
						</thead>
						<tbody>
							{paddingTop > 0 && (
								<tr>
									<td style={{ height: `${paddingTop}px` }} />
								</tr>
							)}
							{virtualRows.map((virtualRow) => {
								const { rows } = tanstackTable.getRowModel();
								const row = rows[virtualRow.index];
								if (!row) {
									return;
								}
								return (
									<tr key={row.id}>
										{row.getVisibleCells().map((cell) => {
											return (
												<td key={cell.id}>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext()
													)}
												</td>
											);
										})}
									</tr>
								);
							})}
							{paddingBottom > 0 && (
								<tr>
									<td style={{ height: `${paddingBottom}px` }} />
								</tr>
							)}
						</tbody>
					</table>
				</div>
				<br />
				<div>{customDetailsData.length} Rows</div>
			</TableWrapper>
		</Box>
	);
};

const TableWrapper = styled.div`
	border: 1px solid lightgray;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: 2px 4px;
		text-align: left;
	}

	td {
		padding: 6px;
	}

	.table_container {
		border: 1px solid lightgray;
		height: 40rem;
		overflow: auto;
	}
`;

const SectionWrapper = styled.div`
	margin: 1rem 0 1rem 0;
	display: flex;
	gap: 1rem;
	flex-direction: column;
`;

const BulkCreateSection = styled.section`
	display: flex;
	gap: 1rem;
`;
const BulkUploadEditWrapper = styled.div``;
const _CustomDetailsExport = memo(CustomDetails);

export { _CustomDetailsExport as CustomDetails };
